/**
 * @author Nihar <nihar.baishya@314ecorp.com>
 * @description Professional Claim Request Form Tables
 */
// todo: uncomment validators when data is available
import _ from 'lodash';
import { Field, useForm } from '@formily/react';
import { FormItem } from '@formily/antd-v5';
import { Plus, Delete } from '@icon-park/react';
import { Table, Input, Button as AntdButton, Select } from 'antd';

import Button from 'components/widgets/button';
import fhirSystemMapping from 'app-constants/eligibility/fhirSystemMapping.json';
import { DatePicker } from 'lib/antd';
import { customLabelMapping } from 'app-constants/eligibility/fhirLabelMapping';
import { formatCurrency } from 'utils/admin/payments';
import { getSecondaryId } from 'utils/claims/claimRequest';
import { CODE_SET_MAPPING } from 'app-constants/codeExplorer';
import { TitleWidget, RequiredTitle } from 'components/codeExplorer/CodeWidgets';
import { MODIFIER_CODES_KEYS } from 'app-constants/claims/claimRequest';
import { QUAL_DROPDOWN_OPTIONS } from 'app-constants/claims/claimFiling';
import { CodeInput } from './InstitutionalClaimRequestFormTables';

type IServiceLineTableItem = ProfessionalClaimParser.ILineItem & { key: string };

// Service lines table
const ServiceLinesTable: React.FC<{
	value: ProfessionalClaimParser.IResponse;
	codeDetails?: CodeExplorer.ICodeSummaryResponse;
	isEditable?: boolean;
	modifierOptions?: IMultiSelectOption[];
	onChange?: (value: ProfessionalClaimParser.IResponse) => void;
}> = ({ value, codeDetails, isEditable = false, modifierOptions, onChange }) => {
	const items = value?.lineItems || [];
	const form = useForm();

	const dataSource: IServiceLineTableItem[] = _.map(items, (item, index) => ({
		...item,
		key: index.toString(),
	}));

	const updateFieldValue = (updater: (currentValue: ProfessionalClaimParser.ILineItem[]) => ProfessionalClaimParser.ILineItem[]) => {
		const newItems = updater(_.cloneDeep(items));
		if (onChange) {
			const updatedValue = { ...value, lineItems: newItems };
			onChange(updatedValue);
		}
	};

	const handleEmergencyIndicatorChange = (index: number, newValue: string) => {
		updateFieldValue((currentValue) => {
			const updatedArray = [...currentValue];
			updatedArray[index].emergencyIndicator = newValue;
			return updatedArray;
		});
	};

	const handleDiagnosisPointerChange = (index: number, newValue: string) => {
		updateFieldValue((currentValue) => {
			const updatedArray = [...currentValue];
			updatedArray[index].diagnosisPointer = newValue;
			return updatedArray;
		});
	};

	const handleEpsdtFamilyPlanChange = (index: number, newValue: string) => {
		updateFieldValue((currentValue) => {
			const updatedArray = [...currentValue];
			updatedArray[index].epsdtFamilyPlan = newValue;
			return updatedArray;
		});
	};

	const addRow = () => {
		const newRow: ProfessionalClaimParser.ILineItem = {
			id: _.uniqueId('newItem_'),
			careTeamSequence: [],
			servicePeriod: { start: '', end: '' },
			placeOfService: '',
			emergencyIndicator: '',
			daysOrUnits: { value: null, code: 'UN' },
			netAmount: { value: null, currency: 'USD' },
			HCPCSCode: { code: null, system: fhirSystemMapping.hcpcsCode },
			modifiers: _.map(MODIFIER_CODES_KEYS, (key) => ({ key, code: null, system: fhirSystemMapping.hcpcsCode })),
			diagnosisPointer: '',
			renderingProvider: null,
			epsdtFamilyPlan: '',
			outsideLabExpanse: null,
		};

		updateFieldValue((currentValue) => [...currentValue, newRow]);
	};

	const deleteRow = (index: number) => {
		const fieldPattern = `row10.serviceLineInfo.info.lineItems.${index}.*`;

		const fieldsToDestroy = form.query(fieldPattern);
		fieldsToDestroy.forEach((field) => {
			if (field && _.isFunction(field.destroy)) {
				field.destroy(true);
			}
		});

		updateFieldValue((currentValue) => _.filter(currentValue, (__, idx) => idx !== index));
		const newLength = items.length - 1;
		const originalLength = items.length;
		for (let i = newLength; i < originalLength; i++) {
			const orphanedFieldPattern = `row10.serviceLineInfo.info.lineItems.${i}.*`;
			form.query(orphanedFieldPattern).forEach((field) => {
				if (field && _.isFunction(field.destroy)) {
					field.destroy(true);
				}
			});
		}
	};

	const columns = [
		{
			title: '',
			maxWidth: 30,
			render: (__: AnyType, _record: AnyType, index: number) => `${index + 1}.`,
		},
		{
			title: 'A. Date(s) of service',
			children: [
				{
					title: <RequiredTitle title='From' required={isEditable} />,
					dataIndex: 'servicePeriod',
					key: 'fromDate',
					width: 150,
					render: (__: AnyType, record: IServiceLineTableItem, index: number) => {
						if (isEditable) {
							return (
								<Field
									name={`lineItems.${index}.servicePeriod.start`}
									validator={[
										{
											required: true,
										},
									]}
									decorator={[FormItem, { feedbackLayout: 'none' }]}
									component={[DatePicker, { placeholder: 'MM/DD/YYYY' }]}
								/>
							);
						}
						return record.servicePeriod?.start || '';
					},
				},
				{
					title: <RequiredTitle title='To' required={isEditable} />,
					dataIndex: 'servicePeriod',
					key: 'toDate',
					width: 150,
					render: (__: AnyType, record: IServiceLineTableItem, index: number) => {
						if (isEditable) {
							return (
								<Field
									name={`lineItems.${index}.servicePeriod.end`}
									validator={[
										{
											required: true,
										},
									]}
									decorator={[FormItem, { feedbackLayout: 'none' }]}
									component={[DatePicker, { placeholder: 'MM/DD/YYYY' }]}
								/>
							);
						}
						return record.servicePeriod?.end || '';
					},
				},
			],
		},
		{
			title: 'B.',
			children: [
				{
					title: 'Place of Service',
					dataIndex: 'placeOfService',
					key: 'placeOfService',
					width: 150,
					render: (__: AnyType, record: IServiceLineTableItem, index: number) => {
						if (isEditable) {
							return (
								<Field
									name={`lineItems.${index}.placeOfService`}
									// validator={[
									// 	{
									// 		required: true,
									// 		message: 'Required',
									// 	},
									// ]}
									decorator={[FormItem]}
									component={[Input, { placeholder: 'Place of Service' }]}
								/>
							);
						}
						return record.placeOfService || '';
					},
				},
			],
		},
		{
			title: 'C.',
			children: [
				{
					title: 'EMG',
					dataIndex: 'emergencyIndicator',
					key: 'emergencyIndicator',
					width: 100,
					render: (__: AnyType, record: IServiceLineTableItem, index: number) => {
						if (isEditable) {
							return (
								<Input
									value={record.emergencyIndicator || ''}
									onChange={(e) => handleEmergencyIndicatorChange(index, e.target.value)}
									placeholder='EMG'
								/>
							);
						}
						return record.emergencyIndicator || '';
					},
				},
			],
		},
		{
			title: 'D. Procedures, services, or supplies',
			children: [
				{
					title: (
						<TitleWidget
							title='CPT/HCPCS'
							codeSetSystem={[CODE_SET_MAPPING.CPT, CODE_SET_MAPPING.HCPCS]}
							showSearchButton={isEditable}
							showRequiredIndicator={isEditable}
						/>
					),
					dataIndex: 'HCPCSCode',
					key: 'cptHcpcs',
					width: 100,
					render: (__: AnyType, record: IServiceLineTableItem, index: number) => {
						if (isEditable) {
							return (
								<Field
									name={`lineItems.${index}.HCPCSCode.code`}
									validator={[
										{
											required: true,
										},
									]}
									decorator={[FormItem, { feedbackLayout: 'none' }]}
									component={[CodeInput, { placeholder: 'CPT/HCPCS' }]}
								/>
							);
						}
						const codeDetailsItem = _.find(codeDetails?.result, { code: record.HCPCSCode?.code });
						if (codeDetailsItem) {
							return <Button.ShowClaimCode codeDetails={codeDetailsItem} />;
						}
						return record.HCPCSCode?.code || '';
					},
				},
				{
					title: 'Modifiers',
					children: [
						{
							title: 'A',
							dataIndex: 'modifiers',
							key: 'modifierA',
							width: 100,
							render: (__: AnyType, record: IServiceLineTableItem, index: number) => {
								const modifierA = _.find(record?.modifiers, { key: 'A' });
								if (isEditable) {
									return (
										<Field
											name={`lineItems.${index}.modifiers.0.code`}
											decorator={[FormItem]}
											component={[
												Select,
												{
													options: modifierOptions,
													showSearch: true,
													placeholder: 'Select modifier',
													allowClear: true,
												},
											]}
										/>
									);
								}
								const codeDetailsItem = _.find(codeDetails?.result, { code: modifierA?.code });
								if (codeDetailsItem) {
									return <Button.ShowClaimCode codeDetails={codeDetailsItem} />;
								}
								return modifierA?.code || '';
							},
						},
						{
							title: 'B',
							dataIndex: 'modifiers',
							key: 'modifierB',
							width: 100,
							render: (__: AnyType, record: IServiceLineTableItem, index: number) => {
								const modifierB = _.find(record?.modifiers, { key: 'B' });
								if (isEditable) {
									return (
										<Field
											name={`lineItems.${index}.modifiers.1.code`}
											decorator={[FormItem]}
											component={[
												Select,
												{
													options: modifierOptions,
													showSearch: true,
													placeholder: 'Select modifier',
													allowClear: true,
												},
											]}
										/>
									);
								}
								const codeDetailsItem = _.find(codeDetails?.result, { code: modifierB?.code });
								if (codeDetailsItem) {
									return <Button.ShowClaimCode codeDetails={codeDetailsItem} />;
								}
								return modifierB?.code || '';
							},
						},
						{
							title: 'C',
							dataIndex: 'modifiers',
							key: 'modifierC',
							width: 100,
							render: (__: AnyType, record: IServiceLineTableItem, index: number) => {
								const modifierC = _.find(record?.modifiers, { key: 'C' });
								if (isEditable) {
									return (
										<Field
											name={`lineItems.${index}.modifiers.2.code`}
											decorator={[FormItem]}
											component={[
												Select,
												{
													options: modifierOptions,
													showSearch: true,
													placeholder: 'Select modifier',
													allowClear: true,
												},
											]}
										/>
									);
								}
								const codeDetailsItem = _.find(codeDetails?.result, { code: modifierC?.code });
								if (codeDetailsItem) {
									return <Button.ShowClaimCode codeDetails={codeDetailsItem} />;
								}
								return modifierC?.code || '';
							},
						},
						{
							title: 'D',
							dataIndex: 'modifiers',
							key: 'modifierD',
							width: 100,
							render: (__: AnyType, record: IServiceLineTableItem, index: number) => {
								const modifierD = _.find(record?.modifiers, { key: 'D' });
								if (isEditable) {
									return (
										<Field
											name={`lineItems.${index}.modifiers.3.code`}
											decorator={[FormItem]}
											component={[
												Select,
												{
													options: modifierOptions,
													showSearch: true,
													placeholder: 'Select modifier',
													allowClear: true,
												},
											]}
										/>
									);
								}
								const codeDetailsItem = _.find(codeDetails?.result, { code: modifierD?.code });
								if (codeDetailsItem) {
									return <Button.ShowClaimCode codeDetails={codeDetailsItem} />;
								}
								return modifierD?.code || '';
							},
						},
					],
				},
			],
		},
		{
			title: 'E.',
			children: [
				{
					title: 'Diagnosis Pointer',
					dataIndex: 'diagnosisPointer',
					key: 'diagnosisPointer',
					width: 150,
					render: (__: AnyType, record: IServiceLineTableItem, index: number) => {
						if (isEditable) {
							return (
								<Input
									value={record.diagnosisPointer || ''}
									onChange={(e) => handleDiagnosisPointerChange(index, e.target.value)}
									placeholder='Diagnosis Pointer'
								/>
							);
						}
						return record.diagnosisPointer || '';
					},
				},
			],
		},
		{
			title: 'F.',
			children: [
				{
					title: <RequiredTitle title='Charges' required={isEditable} />,
					dataIndex: 'netAmount',
					key: 'charges',
					width: 100,
					render: (__: AnyType, record: IServiceLineTableItem, index: number) => {
						if (isEditable) {
							return (
								<Field
									name={`lineItems.${index}.netAmount.value`}
									validator={[
										{
											required: true,
										},
									]}
									decorator={[FormItem, { feedbackLayout: 'none' }]}
									component={[Input, { type: 'number', placeholder: 'Charges' }]}
								/>
							);
						}
						return formatCurrency(record.netAmount?.value);
					},
				},
			],
		},
		{
			title: 'G.',
			children: [
				{
					title: <RequiredTitle title='Days or Units' required={isEditable} />,
					dataIndex: 'daysOrUnits',
					key: 'daysOrUnits',
					width: 100,
					render: (__: AnyType, record: IServiceLineTableItem, index: number) => {
						if (isEditable) {
							return (
								<Field
									name={`lineItems.${index}.daysOrUnits.value`}
									validator={[
										{
											required: true,
										},
									]}
									decorator={[FormItem, { feedbackLayout: 'none' }]}
									component={[Input, { type: 'number', placeholder: 'Days or Units' }]}
								/>
							);
						}
						return record.daysOrUnits?.value || '';
					},
				},
			],
		},
		{
			title: 'H.',
			children: [
				{
					title: 'EPSDT Family Plan',
					dataIndex: 'epsdtFamilyPlan',
					key: 'epsdtFamilyPlan',
					width: 100,
					render: (__: AnyType, record: IServiceLineTableItem, index: number) => {
						if (isEditable) {
							return (
								<Input
									value={record.epsdtFamilyPlan || ''}
									onChange={(e) => handleEpsdtFamilyPlanChange(index, e.target.value)}
									placeholder='EPSDT Family Plan'
								/>
							);
						}
						return record.epsdtFamilyPlan || '';
					},
				},
			],
		},
		{
			title: 'I.',
			children: [
				{
					title: 'ID Qual',
					dataIndex: 'renderingProvider',
					key: 'idQual',
					width: 150,
					render: (__: AnyType, record: IServiceLineTableItem, index: number) => {
						if (isEditable) {
							return (
								<Field
									name={`lineItems.${index}.renderingProviderIdQual`}
									decorator={[FormItem]}
									component={[
										Select,
										{
											options: QUAL_DROPDOWN_OPTIONS,
											placeholder: 'Select Qual',
											allowClear: true,
											showSearch: true,
										},
									]}
								/>
							);
						}
						return customLabelMapping[getSecondaryId(record.renderingProvider)?.display] || '';
					},
				},
			],
		},
		{
			title: 'J.',
			children: [
				{
					title: 'Rendering Provider ID',
					dataIndex: 'renderingProvider',
					key: 'renderingProviderId',
					width: 150,
					render: (__: AnyType, record: IServiceLineTableItem, index: number) => {
						if (isEditable) {
							return (
								<Field
									name={`lineItems.${index}.renderingProviderId`}
									decorator={[FormItem]}
									component={[Input, { placeholder: 'Provider ID' }]}
								/>
							);
						}
						return getSecondaryId(record.renderingProvider)?.value || '';
					},
				},
			],
		},
		...(isEditable
			? [
					{
						title: '',
						key: 'action',
						render: (__: AnyType, _record: IServiceLineTableItem, index: number) => (
							<AntdButton
								type='default'
								icon={<Delete />}
								onClick={() => deleteRow(index)}
								disabled={_.size(items) <= 1}
								danger
								size='small'
							/>
						),
					},
				]
			: []),
	];

	return (
		<div id='service-lines-table-container' style={{ maxHeight: '400px', overflowY: 'auto' }}>
			<Table
				className='claim-form-table'
				columns={columns}
				dataSource={dataSource}
				pagination={false}
				bordered
				size='small'
				rowKey='key'
				scroll={{ x: 'max-content' }}
			/>
			{isEditable && (
				<div className='mt-4 mb-2 text-center'>
					<AntdButton disabled={_.size(items) >= 50} type='dashed' icon={<Plus />} onClick={addRow} size='large'>
						Add Line Item
					</AntdButton>
				</div>
			)}
		</div>
	);
};

export default ServiceLinesTable;
